'use client';

import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';

interface TurnstileWidgetProps {
    onVerify: (token: string) => void;
    onError?: (err: any) => void;
    onExpire?: () => void;
    theme?: 'light' | 'dark' | 'auto';
    className?: string;
}

declare global {
    interface Window {
        turnstile?: {
            render: (
                container: string | HTMLElement,
                options: {
                    sitekey: string;
                    callback: (token: string) => void;
                    'error-callback'?: (error: any) => void;
                    'expired-callback'?: () => void;
                    theme?: string;
                    action?: string;
                    cData?: string;
                }
            ) => string;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
        onloadTurnstileCallback?: () => void;
    }
}

export default function TurnstileWidget({
    onVerify,
    onError,
    onExpire,
    theme = 'light',
    className = ''
}: TurnstileWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'verified' | 'error'>('loading');
    const [siteKey, setSiteKey] = useState<string>(
        (process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || '').trim()
    );

    useEffect(() => {
        if (siteKey) return;
        let active = true;
        fetch('/api/security/status', { cache: 'no-store' })
            .then((r) => r.json())
            .then((data) => {
                if (!active) return;
                const dynamicKey = typeof data?.turnstile_site_key === 'string' ? data.turnstile_site_key.trim() : '';
                if (dynamicKey) {
                    setSiteKey(dynamicKey);
                } else {
                    setStatus('error');
                }
            })
            .catch(() => {
                if (active) setStatus('error');
            });
        return () => {
            active = false;
        };
    }, [siteKey]);

    useEffect(() => {
        if (!siteKey) return;
        let isMounted = true;

        const renderWidget = () => {
            if (!containerRef.current || !window.turnstile) return;

            // Limpiar si ya había un widget
            if (widgetIdRef.current) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {
                    // Ignore
                }
            }

            try {
                const id = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    theme: theme,
                    callback: (token: string) => {
                        if (!isMounted) return;
                        setStatus('verified');
                        onVerify(token);
                    },
                    'error-callback': (err: any) => {
                        if (!isMounted) return;
                        setStatus('error');
                        if (onError) onError(err);
                    },
                    'expired-callback': () => {
                        if (!isMounted) return;
                        setStatus('ready');
                        if (onExpire) onExpire();
                    }
                });
                widgetIdRef.current = id;
                setStatus('ready');
            } catch (renderErr) {
                console.error('Error al renderizar Turnstile:', renderErr);
                setStatus('error');
            }
        };

        // Si ya está cargado el script en window
        if (window.turnstile) {
            renderWidget();
            return () => {
                isMounted = false;
                if (widgetIdRef.current && window.turnstile) {
                    try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
                }
            };
        }

        // Si no está cargado, inyectar el script oficial de Cloudflare
        const existingScript = document.getElementById('cf-turnstile-script');
        if (!existingScript) {
            const script = document.createElement('script');
            script.id = 'cf-turnstile-script';
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
            script.async = true;
            script.defer = true;
            window.onloadTurnstileCallback = () => {
                renderWidget();
            };
            document.head.appendChild(script);
        } else {
            window.onloadTurnstileCallback = () => {
                renderWidget();
            };
        }

        return () => {
            isMounted = false;
            if (widgetIdRef.current && window.turnstile) {
                try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
            }
        };
    }, [siteKey, theme, onVerify, onError, onExpire]);

    return (
        <div className={`flex flex-col items-center justify-center p-3 rounded-2xl bg-slate-50/80 border border-slate-200/90 shadow-2xs ${className}`}>
            <div className="flex items-center gap-2 mb-2 text-[10px] font-black uppercase tracking-wider text-slate-500">
                {status === 'verified' ? (
                    <span className="text-emerald-600 flex items-center gap-1 font-bold">
                        <ShieldCheck className="size-3.5" />
                        Defensa Perimetral Humana Verificada
                    </span>
                ) : status === 'error' ? (
                    <span className="text-rose-600 flex items-center gap-1 font-bold">
                        <ShieldAlert className="size-3.5" />
                        Reintento Requerido
                    </span>
                ) : (
                    <span className="flex items-center gap-1">
                        <Loader2 className="size-3.5 animate-spin text-blue-600" />
                        Validando Seguridad Anti-Bot
                    </span>
                )}
            </div>

            <div ref={containerRef} className="min-h-[65px] flex items-center justify-center" />
        </div>
    );
}
